import { Signal } from '@crown/signal';
import {
  boundary,
  client,
  server,
  defer,
  each,
  el,
  fragment,
  island,
  region,
  sig,
  stat,
  when,
} from '../ir/constructors';
import { parseGate } from '../ir/props';
import { withRecord } from '../ir/record-scope';
import type {
  BoundaryNode,
  Child,
  ClientNode,
  ServerNode,
  DeferNode,
  Dynamic,
  EachNode,
  ElNode,
  FragmentNode,
  Gateable,
  IfNode,
  Instruction,
  IslandSpec,
  Props,
  Reader,
  RecordScope,
  RegionNode,
  SigNode,
  StaticNode,
} from '../ir/types';
import { isFunction, isNumber, isObjectLike, isString } from '../ir/guards';
import { InstructionError } from '../ir/values';

export type { JSX, TargetedEvent } from './jsx-types';
export type { AriaAttributes, AriaRole, RoleAttribute } from './aria';

// What compiled `.tsrx` templates call. The names are the compiler's, so they
// cannot collide with anything an author declares.
export { template as __crownTemplate, tpl as __crownTpl } from '../ir/constructors';

export interface ComponentProps {
  readonly children?: Child;
}

export type Component<P extends object = ComponentProps> = (props: P) => Instruction;

type JsxProps = Props & ComponentProps;

function childrenOf(props: ComponentProps): Child[] {
  const { children } = props;
  if (children === undefined) return [];
  return Array.isArray(children) ? [...children] : [children];
}

/** Builds an element for a tag name, or calls the component for a function. */
export function jsx(type: string | Component<JsxProps>, props: JsxProps, key?: string): Instruction {
  const withKey: JsxProps = key === undefined ? props : { ...props, key };
  if (isString(type)) return el(type, withKey, ...childrenOf(withKey));
  return type(withKey);
}

export const jsxs = jsx;
export const jsxDEV = jsx;

/** The classic factory form (`jsxFactory`), for transpilers that do not inject the automatic runtime import. */
export function createElement(
  type: string | Component<JsxProps>,
  props: JsxProps | null,
  ...children: readonly Child[]
): Instruction {
  const base: JsxProps = props ?? {};
  if (children.length === 0) return jsx(type, base);
  return jsx(type, { ...base, children: children.length === 1 ? children[0] : children });
}

export function Fragment(props: ComponentProps): FragmentNode {
  return fragment(...childrenOf(props));
}

/** A branch written lazily (`{() => <p/>}`, as the tsrx target emits) or as plain children. */
export type Branch = Child | (() => Child);

export interface WhenProps {
  /** Truthy shows the children, as `if` would; a reader or signal is followed. */
  readonly cond: boolean | Dynamic<unknown>;
  readonly else?: Branch;
  readonly children?: Branch;
}

/**
 * Inside `When`, a zero-argument function child is a lazy branch, never a text
 * reader — and it stays a function, so the side that is not shown is never
 * built. A guard in a template only guards if what it guards runs when the
 * guard passes.
 */
export function When(props: WhenProps): IfNode {
  return when(props.cond ?? false, props.children ?? [], props.else);
}

export interface ForProps<T> {
  readonly each: Dynamic<readonly T[]>;
  /** How a row is known across updates. Without one, by the item's `id` or `key`, else by its place. */
  readonly track?: (item: T, index: number) => string;
  readonly children: (item: T, index: Reader<number>, read: Reader<T>) => Child;
  /** False when the row never reads its index, so none is made for it; by default, whether the row takes one. */
  readonly indexed?: boolean;
}

export function For<T>(props: ForProps<T>): EachNode<T> {
  if (!isFunction(props.children)) {
    throw new InstructionError('<For> takes one function child: (item, index) => …');
  }
  return each(props.each, props.track ?? trackById, props.children, props.indexed === undefined ? undefined : { indexed: props.indexed });
}

/**
 * The key a loop gives a row when its author named none: the item's own `id`
 * or `key`, so a row follows its record through a reorder, an insert or a
 * removal; else its place. Worked out from the item alone, so the server and
 * the browser give a row the same key. An item's identity would not be - a
 * server's objects are not the browser's.
 */
export function trackById<T>(item: T, index: number): string {
  if (isObjectLike(item)) {
    const own = 'id' in item ? item.id : 'key' in item ? item.key : undefined;
    if (isString(own) || isNumber(own)) return `id:${own}`;
  }
  return `#${index}`;
}

export interface RowProps {
  /** The values the component is handed that come from the row's item, in order. */
  readonly reads: () => readonly unknown[];
  readonly children: () => Child;
}

function sameEach(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}

/**
 * A component in a `@for` row that is handed values from the row's item. A
 * component reads its props once, so a kept row that takes a new object under
 * its key would go on showing the old values; this makes the component again
 * when, and only when, one of the values it is handed is another one. A
 * component handed only the item's id is left alone however often the rest of
 * the item changes. The compiler wraps such a component in it; nothing else
 * needs to.
 */
export function Row(props: RowProps): SigNode {
  const reads = new Signal.Computed(props.reads, { equals: sameEach });
  return sig(() => {
    reads.get();
    return Signal.subtle.untrack(props.children);
  });
}

export interface DeferProps {
  readonly pending: Child;
  readonly children: () => Promise<Child>;
}

export function Defer(props: DeferProps): DeferNode {
  if (!isFunction(props.children)) {
    throw new InstructionError('<Defer> takes one function child: async () => …');
  }
  return defer(props.pending, props.children);
}

export interface BoundaryProps extends ComponentProps {
  readonly fallback: (cause: unknown) => Child;
}

export function Boundary(props: BoundaryProps): BoundaryNode {
  return boundary(childrenOf(props), props.fallback);
}

export interface ClientProps {
  /** What the server writes, and what shows until the browser has hydrated. */
  readonly fallback?: Child;
  /** The browser-only content, built when it is shown; the compiler makes it lazy. */
  readonly children?: Branch;
}

/** Content only the browser renders, swapped in after hydration. */
export function Client(props: ClientProps): ClientNode {
  const { children } = props;
  const body = children === undefined ? null : isFunction(children) ? children : () => children;
  return client(body, props.fallback);
}

/** Content only the server renders; the browser adopts it with no bindings. */
export function Server(props: ComponentProps): ServerNode {
  return server(props.children ?? null);
}

export function Static(props: ComponentProps): StaticNode | FragmentNode {
  return stat(...childrenOf(props));
}

export type RegionProps = Omit<RegionNode, 't'>;

export function Region(props: RegionProps): RegionNode {
  return region(props);
}

export interface IslandProps extends IslandSpec {
  readonly children: Instruction;
}

/** A JSX expression is always typed `Instruction`, so the element check happens here. */
export function Island(props: IslandProps): ElNode {
  const { children, ...spec } = props;
  if (children.t !== 'el') throw new InstructionError('<Island> wraps exactly one element');
  return island(spec, children);
}

export interface GateProps {
  readonly can: string;
  /** The record a field gate reads; a field gate without one is denied. */
  readonly record?: RecordScope;
  readonly children: Instruction;
}

function isGateable(node: Instruction): node is Gateable {
  return node.t === 'el' || node.t === 'if' || node.t === 'each' || node.t === 'defer' || node.t === 'region';
}

export function Gate(props: GateProps): Gateable {
  if (!isGateable(props.children)) {
    throw new InstructionError('<Gate> wraps an element, When, For, Defer or Region');
  }
  const gate = parseGate(props.can);
  return { ...props.children, gate: props.record === undefined ? gate : withRecord(gate, props.record, '<Gate record>') };
}
